import React from 'react';

import AOS from 'aos';

import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import './i18n';
import 'aos/dist/aos.css';
import News from 'Components/News';
import NewsDetail from 'Components/NewsDetail';

import CookieBanner from './Components/CookieBanner';
import JobOfferDetail from './Components/JobOfferDetail';
import LanguageSwitcher from './Components/LanguageSwitcher';
import MetaPixel from './Components/MetaPixel';
import Admin from './Pages/Admin';
import Contact from './Pages/Contact';
import Employer from './Pages/Employer';
import Home from './Pages/Home';
import Jobs from './Pages/Jobs';
import ONas from './Pages/ONas';
import PartnerPage from './Pages/Partners';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import Reviews from './Pages/Reviews';
import Team from './Pages/Team';
import Worker from './Pages/Worker';
import { initCookieManager } from './utils/cookieManager';

import './Style/index.css';

const AppContent = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isAdminPage = location.pathname.startsWith('/admin');

  // Function to get the current language from URL
  const getLanguageFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const supportedLangs = ['pl', 'rus', 'ua', 'en'];
    return supportedLangs.includes(pathSegments[0]) ? pathSegments[0] : 'pl';
  };

  // Initialize cookie manager
  React.useEffect(() => {
    initCookieManager();
  }, []);

  // Initialize AOS
  React.useEffect(() => {
    // Add class to disable animations during initial load
    document.body.classList.add('no-scroll-animations');

    // Remove the class after a short delay to enable scroll animations
    setTimeout(() => {
      document.body.classList.remove('no-scroll-animations');

      AOS.init({
        duration: 600,
        easing: 'ease-out-cubic',
        once: false,
        offset: 100,
        delay: 0,
        mirror: false,
        anchorPlacement: 'top-bottom',
        disable: false,
        startEvent: 'DOMContentLoaded',
        initClassName: 'aos-init',
        animatedClassName: 'aos-animate',
        debounceDelay: 50,
        throttleDelay: 99,
      });
    }, 100);
  }, []);

  // Refresh AOS on route change
  React.useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      AOS.refresh();
    }, 100);
  }, [location.pathname]);

  // Ensure i18n language matches the URL
  React.useEffect(() => {
    const urlLang = getLanguageFromPath();
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [location.pathname, i18n]);

  return (
    <div>
      <Routes>
        {/* Default routes (Polish) */}
        <Route path='/' element={<Home />} />
        <Route path='/dla-pracownika' element={<Worker />} />
        <Route path='/dla-pracodawcy' element={<Employer />} />
        <Route path='/oferty-pracy' element={<Jobs />} />
        <Route path='/oferty-pracy/:id' element={<JobOfferDetail />} />
        <Route path='/aktualnosci' element={<News />} />
        <Route path='/aktualnosci/:id' element={<NewsDetail />} />
        <Route path='/o-nas' element={<ONas />} />
        <Route path='/nasz-zespol' element={<Team />} />
        <Route path='/partnerzy' element={<PartnerPage />} />
        <Route path='/kontakt' element={<Contact />} />
        <Route path='/opinie' element={<Reviews />} />
        <Route path='/polityka-prywatnosci' element={<PrivacyPolicy />} />

        {/* Russian routes */}
        <Route path='/rus' element={<Home />} />
        <Route path='/rus/dla-sotrudnika' element={<Worker />} />
        <Route path='/rus/dla-rabotodatelya' element={<Employer />} />
        <Route path='/rus/vakansii' element={<Jobs />} />
        <Route path='/rus/vakansii/:id' element={<JobOfferDetail />} />
        <Route path='/rus/novosti' element={<News />} />
        <Route path='/rus/novosti/:id' element={<NewsDetail />} />
        <Route path='/rus/o-nas' element={<ONas />} />
        <Route path='/rus/nasha-komanda' element={<Team />} />
        <Route path='/rus/partnery' element={<PartnerPage />} />
        <Route path='/rus/kontakt' element={<Contact />} />
        <Route path='/rus/otzyvy' element={<Reviews />} />
        <Route
          path='/rus/politika-konfidencialnosti'
          element={<PrivacyPolicy />}
        />

        {/* Ukrainian routes */}
        <Route path='/ua' element={<Home />} />
        <Route path='/ua/dlya-pratsivnika' element={<Worker />} />
        <Route path='/ua/dlya-robotodavtsya' element={<Employer />} />
        <Route path='/ua/vakansii' element={<Jobs />} />
        <Route path='/ua/vakansii/:id' element={<JobOfferDetail />} />
        <Route path='/ua/novyny' element={<News />} />
        <Route path='/ua/novyny/:id' element={<NewsDetail />} />
        <Route path='/ua/pro-nas' element={<ONas />} />
        <Route path='/ua/nasha-komanda' element={<Team />} />
        <Route path='/ua/partneri' element={<PartnerPage />} />
        <Route path='/ua/kontakt' element={<Contact />} />
        <Route path='/ua/vidhuky' element={<Reviews />} />
        <Route
          path='/ua/politika-konfidencinosti'
          element={<PrivacyPolicy />}
        />

        {/* English routes */}
        <Route path='/en' element={<Home />} />
        <Route path='/en/for-workers' element={<Worker />} />
        <Route path='/en/for-employers' element={<Employer />} />
        <Route path='/en/job-offers' element={<Jobs />} />
        <Route path='/en/job-offers/:id' element={<JobOfferDetail />} />
        <Route path='/en/news' element={<News />} />
        <Route path='/en/news/:id' element={<NewsDetail />} />
        <Route path='/en/about-us' element={<ONas />} />
        <Route path='/en/our-team' element={<Team />} />
        <Route path='/en/partners' element={<PartnerPage />} />
        <Route path='/en/contact' element={<Contact />} />
        <Route path='/en/reviews' element={<Reviews />} />
        <Route path='/en/privacy-policy' element={<PrivacyPolicy />} />

        {/* Admin routes */}
        <Route path='/admin' element={<Admin />} />
      </Routes>
      {!isAdminPage && <LanguageSwitcher />}
      {!isAdminPage && <CookieBanner />}
      {!isAdminPage && <MetaPixel />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
